import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import * as z from 'zod'

import { BannerPreview } from './banner-preview'
import {
  ANNOUNCEMENT_TYPE_LABELS,
  buildDefaultEnglishMessage,
  buildDefaultGermanMessage,
  DEFAULT_ANNOUNCEMENT_LINK_URL,
  DEFAULT_ANNOUNCEMENT_WINDOW_DAYS,
  formatGermanDate,
  getErrorMessage,
  shouldUpdateEnglishMessage,
  shouldUpdateGermanMessage
} from './utils'

import {
  ANNOUNCEMENT_TYPES,
  AnnouncementInput,
  AnnouncementType
} from '@/api/announcements-client'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/use-toast'
import { useAuth } from '@/providers/AuthContextProvider'
import { RoutePaths } from '@/routes/routes'
import {
  useMutationCreateAnnouncement,
  useMutationUpdateAnnouncement,
  useQueryGetAnnouncement
} from '@/services/announcements.service'
import { isPermitted, Permissions } from '@/utils/permission-manager'

const announcementFormSchema = z.object({
  type: z.enum(ANNOUNCEMENT_TYPES),
  list_version: z
    .string()
    .trim()
    .min(1, {
      message: 'Please enter the version/date of the subprocessor list.'
    })
    .max(100),
  effective_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: 'Please pick an effective date.'
  }),
  link_url: z
    .string()
    .trim()
    .url({ message: 'Please enter a valid URL.' })
    .refine((value) => value.startsWith('https://'), {
      message: 'The link must use https://'
    }),
  window_days: z.coerce
    .number({ invalid_type_error: 'Please enter a number of days.' })
    .int()
    .min(1, { message: 'At least 1 day.' })
    .max(365, { message: 'At most 365 days.' }),
  message_de: z
    .string()
    .trim()
    .min(1, { message: 'The German banner message is required.' })
    .max(1000),
  message_en: z.string().trim().max(1000).optional()
})

type AnnouncementFormValues = z.infer<typeof announcementFormSchema>

const toInput = (values: AnnouncementFormValues): AnnouncementInput => ({
  type: values.type,
  list_version: values.list_version,
  effective_date: values.effective_date,
  link_url: values.link_url,
  window_days: values.window_days,
  message: {
    de: values.message_de,
    ...(values.message_en ? { en: values.message_en } : {})
  }
})

export function AnnouncementForm() {
  const { announcementId = '' } = useParams<{ announcementId: string }>()
  const isEdit = Boolean(announcementId)
  const navigate = useNavigate()
  const { userData } = useAuth()
  const canManage = Boolean(
    userData && isPermitted(Permissions.MANAGE_ANNOUNCEMENTS, userData)
  )

  const { data: existing, isLoading: isExistingLoading } =
    useQueryGetAnnouncement(announcementId, { enabled: isEdit })
  const createAnnouncement = useMutationCreateAnnouncement()
  const updateAnnouncement = useMutationUpdateAnnouncement()

  const form = useForm<AnnouncementFormValues>({
    resolver: zodResolver(announcementFormSchema),
    defaultValues: {
      type: 'subprocessor_change',
      list_version: '',
      effective_date: '',
      link_url: DEFAULT_ANNOUNCEMENT_LINK_URL,
      window_days: DEFAULT_ANNOUNCEMENT_WINDOW_DAYS,
      message_de: buildDefaultGermanMessage(undefined),
      message_en: buildDefaultEnglishMessage(undefined)
    },
    mode: 'onChange'
  })

  const previousEffectiveDate = useRef<string | undefined>(undefined)

  // Load the draft into the form (edit mode)
  useEffect(() => {
    if (existing) {
      form.reset({
        type: existing.type,
        list_version: existing.list_version ?? '',
        effective_date: existing.effective_date ?? '',
        link_url: existing.link_url ?? DEFAULT_ANNOUNCEMENT_LINK_URL,
        window_days: existing.window_days ?? DEFAULT_ANNOUNCEMENT_WINDOW_DAYS,
        message_de: existing.message?.de ?? '',
        message_en: existing.message?.en ?? ''
      })
      previousEffectiveDate.current = existing.effective_date
    }
  }, [existing, form])

  // Keep the DE/EN messages in sync with the effective date until they have been edited manually
  const effectiveDate = form.watch('effective_date')

  useEffect(() => {
    if (previousEffectiveDate.current === effectiveDate) {
      return
    }

    const currentMessage = form.getValues('message_de')

    if (
      shouldUpdateGermanMessage(currentMessage, previousEffectiveDate.current)
    ) {
      form.setValue('message_de', buildDefaultGermanMessage(effectiveDate), {
        shouldValidate: true
      })
    }

    if (
      shouldUpdateEnglishMessage(
        form.getValues('message_en'),
        previousEffectiveDate.current
      )
    ) {
      form.setValue('message_en', buildDefaultEnglishMessage(effectiveDate), {
        shouldValidate: true
      })
    }

    previousEffectiveDate.current = effectiveDate
  }, [effectiveDate, form])

  const messageDe = form.watch('message_de')
  const messageEn = form.watch('message_en')
  const linkUrl = form.watch('link_url')
  const formattedEffectiveDate = formatGermanDate(effectiveDate, '')
  const messageMissesDate =
    Boolean(formattedEffectiveDate) &&
    Boolean(messageDe) &&
    !messageDe.includes(formattedEffectiveDate)

  const isSubmitting =
    createAnnouncement.isPending || updateAnnouncement.isPending
  const isReadOnly = isEdit && existing && existing.status !== 'draft'

  const onSubmit = (values: AnnouncementFormValues) => {
    const input = toInput(values)
    const onError = (error: unknown) =>
      toast({
        title: 'Error',
        description: `Could not save the announcement. ${getErrorMessage(
          error
        )}`,
        variant: 'destructive'
      })

    if (isEdit) {
      updateAnnouncement.mutate(
        { announcementId, input },
        {
          onSuccess: (announcement) => {
            toast({ title: 'Draft saved' })
            navigate(
              `${RoutePaths.ANNOUNCEMENTS}/${
                announcement?.id ?? announcementId
              }`
            )
          },
          onError
        }
      )
    } else {
      createAnnouncement.mutate(input, {
        onSuccess: (announcement) => {
          toast({
            title: 'Draft created',
            description: 'Review the draft and publish it when ready.'
          })
          navigate(
            announcement?.id
              ? `${RoutePaths.ANNOUNCEMENTS}/${announcement.id}`
              : RoutePaths.ANNOUNCEMENTS
          )
        },
        onError
      })
    }
  }

  if (!canManage) {
    return (
      <p className="text-sm text-muted-foreground">
        You are not permitted to manage announcements.
      </p>
    )
  }

  if (isReadOnly) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Only drafts can be edited. This announcement is {existing?.status}.
        </p>
        <Button
          onClick={() =>
            navigate(`${RoutePaths.ANNOUNCEMENTS}/${announcementId}`)
          }
          variant="outline"
        >
          Back to announcement
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-primary">
          {isEdit ? 'Edit draft announcement' : 'New announcement'}
        </h1>
        <p className="text-sm text-muted-foreground">
          Saved as a draft. Nothing is shown to customers until you publish it.
        </p>
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <Form {...form}>
          <form className="space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select
                    disabled={isExistingLoading}
                    onValueChange={(value) =>
                      field.onChange(value as AnnouncementType)
                    }
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {ANNOUNCEMENT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {ANNOUNCEMENT_TYPE_LABELS[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-5 md:grid-cols-2">
              <FormField
                control={form.control}
                name="list_version"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>List version</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. 2026-10 / v12" {...field} />
                    </FormControl>
                    <FormDescription>
                      Version or date of the subprocessor list.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="effective_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Effective date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormDescription>
                      Date the new list applies from.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <FormField
                control={form.control}
                name="link_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Link URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={DEFAULT_ANNOUNCEMENT_LINK_URL}
                        type="url"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Target of the &quot;Zur Liste&quot; link.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="window_days"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Window (days)</FormLabel>
                    <FormControl>
                      <Input min={1} step={1} type="number" {...field} />
                    </FormControl>
                    <FormDescription>
                      Banner is shown for this many days after publishing.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="message_de"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message (DE)</FormLabel>
                  <FormControl>
                    <Textarea rows={4} {...field} />
                  </FormControl>
                  <FormDescription>
                    Prefilled from the template and kept in sync with the
                    effective date until you edit it. Use informal
                    &quot;du&quot;, never &quot;Sie&quot;.
                  </FormDescription>
                  {messageMissesDate && (
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      The message does not mention the effective date (
                      {formattedEffectiveDate}).
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message_en"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message (EN, optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="We have updated our list of subprocessors (effective from DD.MM.YYYY). Please review the changes."
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Shown to users with English UI language. Falls back to DE
                    when empty.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex gap-2">
              <Button
                disabled={isExistingLoading}
                loading={isSubmitting}
                type="submit"
              >
                {isEdit ? 'Save draft' : 'Create draft'}
              </Button>
              <Button
                onClick={() =>
                  navigate(
                    isEdit
                      ? `${RoutePaths.ANNOUNCEMENTS}/${announcementId}`
                      : RoutePaths.ANNOUNCEMENTS
                  )
                }
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Form>
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="text-primary">Banner preview</CardTitle>
            <CardDescription>
              How the banner appears in the 360 portal.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <BannerPreview
              linkUrl={linkUrl}
              messageDe={messageDe}
              messageEn={messageEn}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
